import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Check as FaCheck, Users as FaUsers, CalendarDays as FaCalendarAlt, ShieldCheck as FaBriefcase } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';
import GradientButton from '../../components/GradientButton';

const PLANS = [
  {
    id: 'free',
    name: 'Darmowy',
    price: 0,
    workerLimit: 1,
    badge: null,
    features: [
      '1 pracownik',
      'Zarządzanie urlopami',
      'Kalendarz urlopowy',
    ],
    cta: 'Aktualny plan',
    disabled: true,
  },
  {
    id: 'S',
    name: 'Pracodawca S',
    price: 59,
    workerLimit: 10,
    badge: null,
    features: [
      'Do 10 pracowników',
      'Zarządzanie urlopami',
      'Kalendarz urlopowy',
    ],
    cta: 'Wybierz plan S',
    disabled: false,
  },
  {
    id: 'M',
    name: 'Pracodawca M',
    price: 99,
    workerLimit: 25,
    badge: null,
    features: [
      'Do 25 pracowników',
      'Zarządzanie urlopami',
      'Kalendarz urlopowy',
    ],
    cta: 'Wybierz plan M',
    disabled: false,
  },
  {
    id: 'L',
    name: 'Pracodawca L',
    price: 179,
    workerLimit: 50,
    badge: null,
    features: [
      'Do 50 pracowników',
      'Zarządzanie urlopami',
      'Kalendarz urlopowy',
    ],
    cta: 'Wybierz plan L',
    disabled: false,
  },
];

function EmployerPricing() {
  const navigate = useNavigate();
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  const [slots, setSlots] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [error, setError] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [slotsRes, plansRes] = await Promise.all([
          api.get('/employer/slots/'),
          api.get('/subscription/plans/?user_type=employer'),
        ]);
        setSlots(slotsRes.data);
        setPlans(Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data?.results ?? []));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
  const resetLoadingState = () => {
    setPaymentLoading(null);
    setError('');
  };

  const handlePageShow = (event) => {
    // event.persisted === true gdy strona wraca z bfcache (np. przycisk "wstecz" po PayU)
    if (event.persisted) {
      resetLoadingState();
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      resetLoadingState();
    }
  };

  window.addEventListener('pageshow', handlePageShow);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.removeEventListener('pageshow', handlePageShow);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, []);

  const handleBuyPlan = async (plan) => {
    setError('');
    setPaymentLoading(plan.id);

    // Znajdź plan w bazie danych
    const dbPlan = plans.find(p => Math.round(p.price) === plan.price);
    if (!dbPlan) {
      setError('Nie znaleziono planu w bazie. Skontaktuj się z pomocą techniczną.');
      setPaymentLoading(null);
      return;
    }

    try {
    const res = await api.post('/payment/create/', {
      plan_id: dbPlan.id,
    });
    if (res.data?.redirectUri) {
      window.location.href = res.data.redirectUri;
    } else {
      setError('Nie otrzymano linku do płatności. Spróbuj ponownie.');
      setPaymentLoading(null);
    }
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się uruchomić płatności. Spróbuj ponownie.');
      setPaymentLoading(null);
    }
  };

  if (loading) return <Loader />;

  const currentLimit = slots?.worker_limit || 1;
  const usedWorkers = slots?.used_worker_slots || 0;

  const getCurrentPlanName = () => {
    if (currentLimit >= 50) return 'Pracodawca L';
    if (currentLimit >= 25) return 'Pracodawca M';
    if (currentLimit >= 10) return 'Pracodawca S';
    return 'Darmowy';
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        {/* HEADER */}
        <motion.div {...fadeInUp} className="text-center mb-5">
          <h1 className="pub-h2 mb-3">
            Plany dla pracodawców
          </h1>
          <p className="pub-muted" style={{ fontSize: '1.1rem' }}>
            Zarządzaj urlopami swojego zespołu. Zacznij za darmo, skaluj gdy potrzebujesz.
          </p>
        </motion.div>

        {error && (
          <Alert variant="danger" className="border-0 mb-4" style={{ borderRadius: '15px' }}>
            {error}
          </Alert>
        )}

        {/* AKTUALNY STATUS */}
        <motion.div {...fadeInUp} className="mb-5">
          <Card
            className="border-0 shadow-lg"
            style={{
              borderRadius: '20px',
              background: '#fff',
              border: '1px solid var(--pub-line)',
            }}
          >
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col xs={12} md={6}>
                  <h5 className="fw-bold mb-1">Twój aktualny plan</h5>
                  <p className="pub-muted mb-0">
                    <strong>{getCurrentPlanName()}</strong>
                    {' '}— {usedWorkers} / {currentLimit} pracowników
                  </p>
                </Col>
                <Col xs={12} md={6} className="mt-3 mt-md-0">
                  <div style={{
                    height: '10px', background: 'var(--pub-line-soft)',
                    borderRadius: '99px', overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: '99px',
                      background: currentLimit > 0 && usedWorkers / currentLimit > 0.8
                        ? '#b91c1c'
                        : 'var(--pub-accent)',
                      width: currentLimit > 0 ? `${Math.min((usedWorkers / currentLimit) * 100, 100)}%` : '0%',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                  <small className="pub-muted mt-1 d-block">
                    {currentLimit - usedWorkers} wolnych slotów
                  </small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </motion.div>

        {/* KARTY PLANÓW */}
        <Row className="g-4 mb-5">
          {PLANS.map((plan, idx) => {
            const isCurrentPlan = getCurrentPlanName() === plan.name;
            const isDowngrade = plan.workerLimit < currentLimit;

            return (
              <Col key={plan.id} xs={12} sm={6} xl={3}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    className="border-0 shadow-lg h-100"
                    style={{
                      borderRadius: '24px',
                      overflow: 'hidden',
                      border: isCurrentPlan ? '2px solid var(--pub-navy)' : '1px solid var(--pub-line)',
                    }}
                  >
                    {/* HEADER KARTY */}
                    <div className="p-4 text-center" style={{ background: 'var(--pub-bg-alt)', borderBottom: '1px solid var(--pub-line-soft)' }}>
                      {plan.badge && (
                        <Badge bg="light" text="dark" className="mb-2" style={{ fontSize: '0.75rem' }}>
                          {plan.badge}
                        </Badge>
                      )}
                      {isCurrentPlan && (
                        <Badge bg="success" className="mb-2" style={{ fontSize: '0.75rem' }}>
                          Aktywny
                        </Badge>
                      )}
                      <h4 className="fw-bold mb-1">{plan.name}</h4>
                      <div style={{ fontSize: '2rem', fontFamily: 'var(--pub-serif)', color: 'var(--pub-navy)', lineHeight: 1.1 }}>
                        {plan.price === 0 ? 'Gratis' : `${plan.price} zł`}
                      </div>
                      {plan.price > 0 && (
                        <small style={{ opacity: 0.8 }}>/ miesiąc</small>
                      )}
                      <div className="mt-2 d-flex align-items-center justify-content-center gap-1">
                        <FaUsers size={14} />
                        <span style={{ fontSize: '0.9rem' }}>Do {plan.workerLimit} pracowników</span>
                      </div>
                    </div>

                    {/* BODY KARTY */}
                    <Card.Body className="p-4 d-flex flex-column">
                      <ul className="list-unstyled mb-4 flex-grow-1">
                        {plan.features.map((f, i) => (
                          <li key={i} className="d-flex align-items-start gap-2 mb-2">
                            <FaCheck size={14} style={{ color: '#2b8a3e', marginTop: '3px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.9rem', color: '#495057' }}>{f}</span>
                          </li>
                        ))}
                      </ul>

                      {isCurrentPlan ? (
                        <button
                          className="btn w-100"
                          disabled
                          style={{
                            borderRadius: '14px', padding: '0.75rem',
                            background: '#e8f5e9', color: '#2b8a3e',
                            fontWeight: 600, border: 'none',
                          }}
                        >
                          Twój aktualny plan
                        </button>
                      ) : plan.price === 0 ? (
                        <button
                          className="btn w-100"
                          disabled
                          style={{
                            borderRadius: '14px', padding: '0.75rem',
                            background: '#f1f3f5', color: '#868e96',
                            fontWeight: 600, border: 'none',
                          }}
                        >
                          Plan podstawowy
                        </button>
                      ) : (
                        <GradientButton
                          gradient="blue"
                          onClick={() => handleBuyPlan(plan)}
                          disabled={!!paymentLoading}
                          style={{ width: '100%' }}
                        >
                          {paymentLoading === plan.id ? 'Przekierowanie...' : plan.cta}
                        </GradientButton>
                      )}

                      {isDowngrade && !isCurrentPlan && (
                        <small className="text-muted text-center mt-2 d-block">
                          Niższy limit niż aktualny plan
                        </small>
                      )}
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            );
          })}
        </Row>

        {/* INFO O PŁATNOŚCIACH */}
        <motion.div {...fadeInUp}>
          <Card
            className="border-0 shadow-lg"
            style={{
              borderRadius: '20px',
              background: 'rgba(255,255,255,0.04)',
            }}
          >
            <Card.Body className="p-4">
              <Row className="g-4 text-center">
                {[
                  { icon: <FaCalendarAlt size={24} />, title: 'Płatność jednorazowa', desc: 'Bez automatycznych obciążeń. Odnawiasz kiedy chcesz.' },
                  { icon: <FaUsers size={24} />, title: 'Zmiana planu', desc: 'Możesz w każdej chwili przejść na wyższy pakiet.' },
                  { icon: <FaBriefcase size={24} />, title: 'Bezpieczna płatność', desc: 'Płatność przez PayU — BLIK, karta, przelew.' },
                ].map((item, idx) => (
                  <Col key={idx} xs={12} md={4}>
                    <div className="pub-muted mb-2">{item.icon}</div>
                    <h6 className="fw-bold mb-1">{item.title}</h6>
                    <p className="pub-muted mb-0" style={{ fontSize: '0.9rem' }}>{item.desc}</p>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </motion.div>

        {/* KONTAKT */}
        <motion.div {...fadeInUp} className="text-center mt-4">
          <p className="pub-muted">
            Potrzebujesz większego pakietu lub masz pytania?{' '}
            <a href="/contact" className="pub-link">Napisz do nas</a>
          </p>
        </motion.div>

      </Container>
    </div>
  );
}

export default EmployerPricing;